/**
 * External / cross-module navigation helpers for Regulations Next (GRI).
 * Used when Fusion shell, chatbot, newsletter, or in-app links open detail pages via `modulePath`.
 */

import { envConfig } from "../config/env";
import { resolveEffectiveDomain } from "./domainPersistence";
import { isInIframe } from "./parentFrameUtils";

/** Query param values for `source` that tell apiClient to omit the `domain` request header. */
export const NAV_SOURCE_OMIT_DOMAIN = {
  CHATBOT: "chatbot",
  /** Newsletter module deep links — same contract as chatbot (omit domain header). */
  NEWSLETTER: "newsletter",
  RELATED_ITEM: "relatedItem",
  /** Assessment Center AI / Manual Impact grids → regulation detail. */
  ASSESSMENT_CENTER: "assessmentCenter",
} as const;

export type OmitDomainNavSource =
  (typeof NAV_SOURCE_OMIT_DOMAIN)[keyof typeof NAV_SOURCE_OMIT_DOMAIN];

const OMIT_DOMAIN_SOURCES = new Set<string>(
  Object.values(NAV_SOURCE_OMIT_DOMAIN),
);

/** Whether `source` query param means detail APIs omit the `domain` header. */
export function isOmitDomainNavSource(
  source: string | null | undefined,
): source is OmitDomainNavSource {
  const normalized = source?.trim();
  return Boolean(normalized && OMIT_DOMAIN_SOURCES.has(normalized));
}

/**
 * Collect URLs that may carry shell navigation context (self, top, referrer).
 */
export function getNavigationContextUrls(): string[] {
  if (typeof window === "undefined") return [];

  const urls: string[] = [window.location.href];

  try {
    if (window.top && window.top !== window.self) {
      urls.push(window.top.location.href);
    }
  } catch {
    if (typeof document !== "undefined" && document.referrer) {
      urls.push(document.referrer);
    }
  }

  return urls;
}

/**
 * Whether API requests should omit the `domain` header.
 * True when shell or in-app URL includes a known omit-domain `source` (e.g. chatbot, assessmentCenter).
 */
export function shouldOmitDomainHeader(): boolean {
  for (const href of getNavigationContextUrls()) {
    try {
      const source = new URL(href).searchParams.get("source");
      if (isOmitDomainNavSource(source)) {
        return true;
      }
    } catch {
      // ignore invalid URLs
    }
  }
  return false;
}

/** Fusion portal base URL for `…/regulationsnext?modulePath=…` links. */
export function resolvePlatformBaseUrl(): string {
  const configured = envConfig.platformBaseUrl?.trim();
  if (configured) return configured.replace(/\/$/, "");

  try {
    if (typeof document !== "undefined" && document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch {
    // ignore
  }

  return "https://app.devsecops.freyafusion.com";
}

export interface BuildFusionShellUrlOptions {
  /** Sets `source` query param (e.g. chatbot, newsletter, relatedItem). */
  source?: OmitDomainNavSource | string;
  /** Extra query params on the shell URL (not inside modulePath). */
  shellQuery?: Record<string, string>;
}

/**
 * Build a Freya Fusion shell URL that loads GRI at `modulePath`.
 *
 * @example
 * buildFusionShellUrl("/regulation-details/INS206328", { source: "chatbot" })
 * // => https://app…/regulationsnext?modulePath=%2Fregulation-details%2FINS206328&source=chatbot
 *
 * @example Newsletter (same shape as chatbot)
 * buildFusionShellUrl("/regulation-details/INS206328", { source: "newsletter" })
 */
export function buildFusionShellUrl(
  modulePath: string,
  options?: BuildFusionShellUrlOptions,
): string {
  const normalizedPath = modulePath.startsWith("/")
    ? modulePath
    : `/${modulePath}`;
  const params = new URLSearchParams();
  params.set("modulePath", normalizedPath);
  if (options?.source) {
    params.set("source", options.source);
  }
  if (options?.shellQuery) {
    for (const [key, value] of Object.entries(options.shellQuery)) {
      params.set(key, value);
    }
  }
  return `${resolvePlatformBaseUrl()}/regulationsnext?${params.toString()}`;
}

/** Shell URL for Chatbot → regulation detail. */
export function buildChatbotRegulationDetailUrl(regulationId: string): string {
  return buildFusionShellUrl(`/regulation-details/${regulationId}`, {
    source: NAV_SOURCE_OMIT_DOMAIN.CHATBOT,
  });
}

/** Shell URL for Newsletter → regulation detail (same contract as chatbot). */
export function buildNewsletterRegulationDetailUrl(regulationId: string): string {
  return buildFusionShellUrl(`/regulation-details/${regulationId}`, {
    source: NAV_SOURCE_OMIT_DOMAIN.NEWSLETTER,
  });
}

/** In-app React Router path for regulation detail (optional version / source / domain). */
export function buildRegulationDetailPath(
  regulationId: string,
  options?: {
    version?: string;
    source?: OmitDomainNavSource | string;
    /** Product domain code/name for detail APIs (e.g. from global search). */
    domain?: string;
  },
): string {
  const params = new URLSearchParams();
  if (options?.version) {
    const v = options.version.startsWith("v")
      ? options.version
      : `v${options.version}`;
    params.set("version", v);
  }
  if (options?.source) {
    params.set("source", options.source);
  }
  const domain = isOmitDomainNavSource(options?.source)
    ? null
    : resolveEffectiveDomain({ explicit: options?.domain });
  if (domain) {
    params.set("domain", domain);
  }
  const qs = params.toString();
  return `/regulation-details/${regulationId}${qs ? `?${qs}` : ""}`;
}

/**
 * Browser-facing href for regulation detail links (e.g. right-click → open in new tab).
 * In Fusion iframe, returns shell URL with modulePath; otherwise in-app path for SPA navigation.
 */
export function buildRegulationDetailHref(
  regulationId: string,
  options?: Parameters<typeof buildRegulationDetailPath>[1],
): string {
  const modulePath = buildRegulationDetailPath(regulationId, options);
  if (isInIframe()) {
    return buildFusionShellUrl(modulePath);
  }
  return modulePath;
}

/** Query string for detail URL with selected keys removed (keeps `domain`, `source`, etc.). */
export function buildRegulationDetailSearchPreserving(
  searchParams: URLSearchParams,
  omitKeys: readonly string[] = ["version"],
): string {
  const next = new URLSearchParams();
  for (const [key, value] of searchParams.entries()) {
    if (omitKeys.includes(key)) continue;
    next.set(key, value);
  }
  const qs = next.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Platform My Task URL (`{platformOrigin}/mytask`).
 * Domain varies by environment (e.g. https://app.onerims-dev.freyrapps.com/mytask).
 */
export function buildMyTaskUrl(): string {
  return `${resolvePlatformBaseUrl()}/mytask`;
}

/**
 * Navigate to platform My Task. Uses the top window when GRI runs in Fusion iframe.
 */
export function navigateToMyTask(): void {
  const url = buildMyTaskUrl();
  try {
    if (typeof window !== "undefined" && window.top && window.top !== window.self) {
      window.open(url, "_top");
      return;
    }
  } catch {
    // Cross-origin top access can throw; fall through to same-window navigation.
  }
  if (typeof window !== "undefined") {
    window.location.href = url;
  }
}

/** Domain from a global-search record `source` payload. */
export function resolveGlobalSearchSourceDomain(
  source: Record<string, unknown>,
): string | undefined {
  const domainName = source.domain_name;
  if (typeof domainName === "string" && domainName.trim()) {
    return domainName.trim();
  }
  const domainId = source.domain_id;
  if (domainId && typeof domainId === "object" && "name" in domainId) {
    const nested = (domainId as { name?: unknown }).name;
    if (typeof nested === "string" && nested.trim()) {
      return nested.trim();
    }
  }
  if (typeof domainId === "string" && domainId.trim()) {
    return domainId.trim();
  }
  return undefined;
}
